"use client";

import {
  forwardRef,
  ReactNode,
  RefObject,
  useEffect,
  useRef,
  useState,
} from "react";
import { Select as RadixSelect } from "radix-ui";

import { BREAKPOINTS } from "../../constants";
import { useBreakpoint } from "../../hooks/useBreakpoint";
import { csx } from "../../utils";
import { ActionMenu, ActionMenuAction } from "../ActionMenu/ActionMenu";

import "./Select.css";
import "../Menu/Menu.css";
import { useTheme } from "../ThemeProvider";

interface SelectButtonProps {
  disabled?: boolean;
  isOpen?: boolean;
  children?: ReactNode;
  onClick?: () => void;
  isRounded?: boolean;
}

const SelectButton = forwardRef<HTMLButtonElement, SelectButtonProps>(
  (
    { disabled, isOpen, children, onClick, isRounded, ...props },
    ref: RefObject<HTMLButtonElement>
  ) => {
    return (
      <button
        {...props}
        ref={ref}
        disabled={disabled}
        onClick={onClick}
        className={csx(
          "proton-Select__trigger",
          disabled && "proton-Select__trigger--disabled",
          !isOpen && "proton-Select__trigger--closed",
          isOpen && "proton-Select__trigger--opened",
          isRounded && "proton-Select__trigger--rounded"
        )}
      >
        {children}
      </button>
    );
  }
);
SelectButton.displayName = "ProtonUISelectButton";

export interface SelectItem {
  /** The label to display for the item */
  label: string;

  /** The key of the item */
  key: string;

  /** The URL to navigate to when the item is clicked, turns item into an a tag */
  to?: string;
}

export interface SelectProps {
  /** Array of keys that should be disabled
   * @example ["Thing 1", "Thing 2"]
   */
  disabledKeys?: string[];

  /** The key of the default selected item */
  defaultSelectedKey?: string;

  /** Test ID for the select */
  "data-testid"?: string;

  /** Whether the select is disabled */
  isDisabled?: boolean;

  /** Array of items to display
   * @example [{ key: "thing-1", label: "Thing 1" }, { key: "thing-2", label: "Thing 2" }]
   * @see {@link SelectItem}
   */
  items: SelectItem[];

  /** Whether the select button is rounded
   * @default false
   */
  isRounded?: boolean;

  /** Label to display above the select
   * @external https://developer.mozilla.org/en-US/docs/Web/HTML/Element/label#text
   */
  label?: string;

  /** The name of the select field */
  name: string;

  /** Callback fired when the menu closes */
  onClose?: () => void;

  /** Callback fired when the menu opens */
  onOpen?: () => void;

  /** Callback fired when selection changes */
  onSelectionChange?: (key: string) => void;

  /** Currently selected key */
  selectedKey?: string;
}

/**
 * A dropdown select menu that opens a popover on desktop and an action menu on mobile.
 * Supports controlled and uncontrolled modes.
 *
 * API:
 *  - {@link SelectProps}
 */
export function Select({
  label,
  name,
  isDisabled,
  disabledKeys,
  selectedKey,
  onSelectionChange,
  defaultSelectedKey,
  onOpen,   
  onClose,
  items,
  isRounded = false,
  "data-testid": testId,
}: SelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [internalSelectedKey, setInternalSelectedKey] = useState(
    selectedKey || defaultSelectedKey || ""
  );
  const { className: themeClass, style: themeStyle } = useTheme();
  const isMobile = useBreakpoint(BREAKPOINTS.MEDIUM);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const hiddenInputRef = useRef<HTMLInputElement>(null);

  // Handle controlled vs uncontrolled state
  const currentSelectedKey =
    selectedKey !== undefined ? selectedKey : internalSelectedKey;
  const selectedItem = items.find((item) => item.key === currentSelectedKey);

  const handleValueChange = (value: string) => {
    if (selectedKey === undefined) {
      setInternalSelectedKey(value);
    }
    onSelectionChange?.(value);

    // Update hidden input for form submission
    if (hiddenInputRef.current) {
      hiddenInputRef.current.value = value;
      hiddenInputRef.current.dispatchEvent(
        new Event("change", { bubbles: true })
      );
    }
  };

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (open) {
      onOpen?.();
    } else {
      onClose?.();
    }
  };

  // Update internal state when controlled selectedKey changes
  useEffect(() => {
    if (selectedKey !== undefined) {
      setInternalSelectedKey(selectedKey);
    }
  }, [selectedKey]);

  const actionItems: ActionMenuAction[] = items.map((item) => ({
    key: item.key,
    label: item.label,
    to: item.to,
    onAction: () => {
      handleValueChange(item.key);
      setIsOpen(false);
    },
  }));

  if (isMobile) {
    return (
      <div
        className="proton-Select"
        aria-label={label || name}
        aria-disabled={isDisabled}
      >
        {label && (
          <div className="proton-Select__label" data-testid="select-label">
            {label}
          </div>
        )}

        {/* Hidden input for form submission */}
        <input
          ref={hiddenInputRef}
          type="hidden"
          name={name}
          value={currentSelectedKey}
          data-testid="hidden-select-container"
        />

        <SelectButton
          isOpen={isOpen}
          isRounded={isRounded}
          ref={triggerRef}
          disabled={isDisabled}
          data-testid={testId}
          onClick={() => setIsOpen(true)}
        >
          <div className="proton-Select__value">
            {selectedItem ? selectedItem.label : "Select an option"}
          </div>
          <div
            className={csx(
              "proton-Select__trigger_icon",
              isOpen && "proton-Select__trigger_icon--flipped"
            )}
            aria-hidden="true"
          >
            <svg id="caret-down" viewBox="0 0 512 512">
              <title>{isOpen ? "caret down" : "caret up"}</title>
              <path
                fill="currentColor"
                d="M233.4 406.6c12.5 12.5 32.8 12.5 45.3 0l192-192c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L256 338.7L86.6 169.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3l192 192z"
              />
            </svg>
          </div>
        </SelectButton>

        <ActionMenu
          isOpen={isOpen}
          onClose={() => setIsOpen(false)}
          actions={actionItems}
          selectedKeys={[currentSelectedKey]}
          disabledKeys={disabledKeys}
        />
      </div>
    );
  }

  return (
    <div
      className="proton-Select"
      aria-label={label || name}
      aria-disabled={isDisabled}
    >
      {label && (
        <div className="proton-Select__label" data-testid="select-label">
          {label}
        </div>
      )}

      {/* Hidden input for form submission */}
      <input
        ref={hiddenInputRef}
        type="hidden"
        name={name}
        value={currentSelectedKey}
        data-testid="hidden-select-container"
      />

      <RadixSelect.Root
        value={currentSelectedKey}
        onValueChange={handleValueChange}
        onOpenChange={handleOpenChange}
        disabled={isDisabled}
      >
        <RadixSelect.Trigger asChild>
          <SelectButton
            isRounded={isRounded}
            isOpen={isOpen}
            ref={triggerRef}
            disabled={isDisabled}
            data-testid={testId}
          >
            <RadixSelect.Value
              className="proton-Select__value"
              placeholder="Select an option"
            >
              {selectedItem ? selectedItem.label : "Select an option"}
            </RadixSelect.Value>
            <RadixSelect.Icon asChild>
              <div
                className={csx(
                  "proton-Select__trigger_icon",
                  isOpen && "proton-Select__trigger_icon--flipped"
                )}
                aria-hidden="true"
              >
                <svg id="caret-down" viewBox="0 0 512 512">
                  <title>{isOpen ? "caret down" : "caret up"}</title>
                  <path
                    fill="currentColor"
                    d="M233.4 406.6c12.5 12.5 32.8 12.5 45.3 0l192-192c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L256 338.7L86.6 169.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3l192 192z"
                  />
                </svg>
              </div>
            </RadixSelect.Icon>
          </SelectButton>
        </RadixSelect.Trigger>

        <RadixSelect.Portal>
          <RadixSelect.Content
            className={csx(
              "proton-Menu",
              "proton-MenuTrigger__menu",
              themeClass
            )}
            style={themeStyle as React.CSSProperties}
            position="popper"
          >
            <RadixSelect.Viewport>
              {items.map((item) => (
                <RadixSelect.Item
                  key={item.key}
                  value={item.key}
                  disabled={disabledKeys?.includes(item.key)}
                  className="proton-Menu__item"
                >
                  <RadixSelect.ItemText>{item.label}</RadixSelect.ItemText>
                </RadixSelect.Item>
              ))}
            </RadixSelect.Viewport>
          </RadixSelect.Content>
        </RadixSelect.Portal>
      </RadixSelect.Root>
    </div>
  );
}

Select.displayName = "ProtonUISelect";
